import { ReactNode } from "react";

import { Payment } from "./Payment";
import { PayService } from "./PayService";
import { Settle } from "./Settle";
import { Merchant } from "./Merchant";
import { Sales } from "./Sales";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";
type CheckRowProps = {
  content: ReactNode | ReactNode[];
};

export const MobileCard = ({ title, src, text }: { title: string; src: string; text?: string }) => {
  const isGradient = title === "온라인 결제" || title === "EBPP";

  //  공통 카드 스타일(기존 유지)
  const baseClass = "flex flex-col rounded-[16px] py-[32px] px-[24px] gap-[12px] bg-white";

  if (!isGradient) {
    return (
      <div
        className={`${baseClass}`}
        style={{
          boxShadow: "inset 0 0 0 1px #DDE0E6",
          border: "4px solid transparent",
        }}
      >
        <img src={src} width={56} height={56} alt="icon" />
        <div className="flex flex-col">
          <span className="font-bold text-[18px]">{title}</span>
          {text && <span className="font-normal text-[12px]">{text}</span>}
        </div>
      </div>
    );
  }

  //  온라인 결제: 부분 그라데이션 보더
  return (
    <div
      className={baseClass}
      style={{
        // 회색 보더는 inset shadow로 유지(겹침 방지)
        boxShadow: "inset 0 0 0 1px #DDE0E6",

        // 그라데이션 보더 두께
        border: "4px solid transparent",

        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",

        // 안쪽 흰색 + 바깥(보더)에만 conic 그라데이션
        backgroundImage:
          title === "온라인 결제"
            ? `
          linear-gradient(#ffffff, #ffffff),
          conic-gradient(
            from 275deg,
            #0079FF 0deg,
            #00FF44 80deg,
            transparent 70deg,
            transparent 360deg
          )
        `
            : title === "EBPP"
              ? `
          linear-gradient(#ffffff, #ffffff),
          conic-gradient(
            from 90deg,
            #0079FF 0deg,
            #00FF44 80deg,
            transparent 70deg,
            transparent 360deg
          )
        `
              : "",
      }}
    >
      <img src={src} width={56} height={56} alt="icon" />
      <div className="flex flex-col gap-[4px]">
        <span className="font-bold text-[18px]">{title}</span>
        {text && <span className="font-normal text-[14px]">{text}</span>}
      </div>
    </div>
  );
};

export const MobileCard2 = ({
  src,
  title,
  text,
}: {
  src: string;
  title: string | ReactNode;
  text: ReactNode | ReactNode[];
}) => {
  const items = Array.isArray(text) ? text : [text];
  return (
    <div
      className="flex flex-col items-center justify-center  md:p-10 h-full py-[32px] px-[24px] md:h-full rounded-[16px]"
      style={{
        boxShadow: "inset 0 0 0 1px #DDE0E6",
        border: "3px solid transparent",
      }}
    >
      <div className="flex flex-col gap-[24px] items-start w-full">
        <img src={src} alt="icon1" width={56} height={56} />

        <div className="flex flex-col gap-[12px] md:gap-5">
          <span className="md:h-[94px] font-bold text-[18px] md:text-[36px]">{title}</span>
          <CheckRow
            content={items.map((t, i) => (
              <span
                key={i}
                className="font-normal text-[16px] md:text-[18px] text-[#4D4F58] leading-[1.5]"
              >
                {t}
              </span>
            ))}
          />
        </div>
      </div>
    </div>
  );
};

export const CheckRow = ({ content }: CheckRowProps) => {
  const isMobile = useResponsiveMobile(900);
  // 배열/단일 모두 처리
  const items = Array.isArray(content) ? content : [content];

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex flex-row gap-1 items-start">
          <img
            src="/assets/images/icon/check.png"
            alt="check"
            width={isMobile ? 16 : 24}
            height={isMobile ? 16 : 24}
            className="shrink-0"
          />
          {item}
        </div>
      ))}
    </div>
  );
};

export const IconCol = ({ src, service }: { src: string; service: string }) => (
  <div className="flex flex-col items-center gap-5 flex-[1_1_140px] min-w-[140px]">
    <img src={src} alt="payment_service" width={56} height={56} />
    <span className="font-normal text-[28px] md:text-heading1_2">{service}</span>
  </div>
);

export const Section1 = ({
  refs,
}: {
  refs: {
    payment: React.RefObject<HTMLDivElement>;
    payService: React.RefObject<HTMLDivElement>;
    settle: React.RefObject<HTMLDivElement>;
    merchant: React.RefObject<HTMLDivElement>;
    sales: React.RefObject<HTMLDivElement>;
  };
}) => {
  const isMobile = useResponsiveMobile(900);
  return (
    <section className="flex flex-col gap-[110px] md:gap-[200px] px-4 md:px-0 overflow-x-hidden">
      {/* section1 */}
      <Payment refs={refs} />

      {isMobile && <div className="h-[1px] w-full bg-[#DDE0E6]" />}

      {/* section2 */}
      <Settle refs={refs} />

      {isMobile && <div className="h-[1px] w-full bg-[#DDE0E6]" />}

      {/* section3 */}
      <Merchant refs={refs} />

      {isMobile && <div className="h-[1px] w-full bg-[#DDE0E6]" />}

      {/* section4 */}
      <PayService refs={refs} />

      {isMobile && <div className="h-[1px] w-full bg-[#DDE0E6]" />}

      {/* section5 */}
      <Sales refs={refs} />
    </section>
  );
};
